import { useEffect, useState } from "react";
import { useApi } from "../../utils/Api";
import { useNavigate, useParams } from "react-router-dom";
import Translate from "../../utils/Translate";
import DigitalLicenseListItem from "../../components/digital-licenses/DigitalLicenseListItem";
import Preloader from "../../components/Preloader";
import FeatureBox from "../../components/digital-licenses/FeatureBox";
import DigitalLicenseProfilePictureDialog from "../../components/digital-licenses/DigitalLicenseProfilePictureDialog";
import { accessKeys, hasAccess } from "../../utils/userAccess";
import { detectBrowser, detectDevice } from "../../utils/device";
import { androidAppUrl, iosAppUrl } from "../../utils/constants";
import { Button } from "@mui/material";

export default function DigitalLicenseListPage({ isPublishGroup }) {
  const [licenses, setLicenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDigitalLicenseProfilePicDialog, setShowDigitalLicenseProfilePicDialog] = useState(false);
  const [profile, setProfile] = useState(null);
  const api = useApi();
  const navigate = useNavigate();
  const { id: digitalLicensePublishGroupId } = useParams();

  useEffect(() => {
    async function loadLicenses() {
      setIsLoading(true);
      const url = isPublishGroup
        ? `${import.meta.env.VITE_MAIN_URL}v1/digital/licenses/groups/${digitalLicensePublishGroupId}`
        : `${import.meta.env.VITE_MAIN_URL}v1/digital/licenses`;
      const response = await api.fetch(url, false, "GET");
      if (response.isSuccessful) {
        setLicenses(response.licenses);
      }
      setIsLoading(false);
    }

    loadLicenses();
  }, [api, isPublishGroup, digitalLicensePublishGroupId]);

  async function loadProfilePicUrl() {
    const response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}ecomp/profiles`, false, "GET");
    if (response.isSuccessful) {
      setProfile({ profilePictureUrl: response.profilePictureDataUrl });
    }
  }

  async function handleClickProfileImageFeatureBox() {
    setIsLoading(true);
    await loadProfilePicUrl();
    setShowDigitalLicenseProfilePicDialog(true);
    setIsLoading(false);
  }

  function getAppUrl() {
    const device = detectDevice();
    switch (device) {
      case "ios":
        return iosAppUrl;
      case "android":
        return androidAppUrl;
      case "desktop":
      default:
        const browser = detectBrowser();
        return browser === "safari" ? iosAppUrl : androidAppUrl;
    }
  }

  return (
    <>
      {showDigitalLicenseProfilePicDialog && profile && (
        <DigitalLicenseProfilePictureDialog
          open
          onClose={() => setShowDigitalLicenseProfilePicDialog(false)}
          onProfilePictureChanged={() => navigate(0)}
          currentImageUrl={profile.profilePictureUrl}
        />
      )}
      {isLoading && <Preloader />}
      <div className="dlcPage">
        {isPublishGroup && <div className="dlcHeading1">{Translate.get("ContactDigitalLicensesHeader")}</div>}

        {licenses && licenses.length > 0 && (
          <div className="dlcList">
            {licenses.map((license) => (
              <DigitalLicenseListItem
                key={license.digitalLicenseId}
                license={license}
                showDigitalPermitButton={false}
                isRestrictedView={isPublishGroup}
              />
            ))}
            <div></div>
          </div>
        )}

        {!isLoading && licenses && licenses.length === 0 && (
          <div className="dlcNoDataContainer withBottomContent">
            <img src="/static/no-data-empty-box.svg" alt="" />
          </div>
        )}

        {!isPublishGroup && hasAccess(accessKeys.digitalLicensesHolder) && (
          <div className="dlcFeatureBoxesContainer">
            <FeatureBox
              color="#006EC8"
              title={Translate.get("ECompProfilePicInfoTitle")}
              description={Translate.get("ECompProfilePicInfoDescription")}
              buttonLabel={Translate.get("ECompProfilePicButtonLabel")}
              imgSrc="/static/profile-pic-info.png"
              imgClassName="dlcFeatureBoxProfilePicImage"
              buttonComponent={
                <Button onClick={handleClickProfileImageFeatureBox} variant="contained" color="primary">
                  {Translate.get("ECompProfilePicButtonLabel")}
                </Button>
              }
            />
            <FeatureBox
              color="#F05032"
              title={Translate.get("ECompAppInfoTitle")}
              description={Translate.get("ECompAppInfoDescription")}
              imgSrc="/static/app-info.png"
              imgClassName="dlcFeatureBoxAppImage"
              buttonComponent={
                detectDevice() === "desktop" ? (
                  <a href={iosAppUrl} target="_blank" rel="noopener noreferrer">
                    <img src="https://eportpublic.blob.core.windows.net/others/apple/get_on_apple_store_SE.svg?sp=r&st=2024-04-12T07:06:58Z&se=2064-04-12T15:06:58Z&spr=https&sv=2022-11-02&sr=c&sig=STfImuoPxA9srWpHhIHlMAaW7w9ST8G1CYcpsinC6PQ%3D" />
                  </a>
                ) : (
                  <Button
                    component="a"
                    href={getAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    color="error"
                  >
                    {Translate.get("ECompAppInfoButtonLabel")}
                  </Button>
                )
              }
              extraButtonComponent={
                detectDevice() === "desktop" ? (
                  <a href={androidAppUrl} target="_blank" rel="noopener noreferrer">
                    <img src="https://eportpublic.blob.core.windows.net/others/google/get_on_google_play_SE.png?sp=r&st=2024-04-12T07:06:58Z&se=2064-04-12T15:06:58Z&spr=https&sv=2022-11-02&sr=c&sig=STfImuoPxA9srWpHhIHlMAaW7w9ST8G1CYcpsinC6PQ%3D" />
                  </a>
                ) : null
              }
            />
          </div>
        )}
      </div>
    </>
  );
}
